/**
 * Full-page banner editor (create + edit) — replaces the cramped modal so the
 * admin gets a large, true-to-life live preview that uses the exact same slide
 * markup as the public carousel (`BannerSlideView`). Image-first: upload + crop
 * to 16:9, optional text overlay, CTAs. Gated by BANNER.MANAGE.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ImagePlus, X, Eye, Maximize2 } from 'lucide-react';
import { request, api } from '../../api/client';
import { Button, Input, Textarea, Field, Switch, toast, cn } from '../../ui';
import { ImageCropper } from '../../components/ImageCropper';
import { BannerSlideView, type Banner } from '../../site/BannerCarousel';

interface BannerRow extends Banner {
  sortOrder: number;
  isActive: boolean;
  showOverlay: boolean;
}

type Draft = {
  id?: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  showOverlay: boolean;
  ctaLabel: string;
  ctaLink: string;
  secondaryCtaLabel: string;
  secondaryCtaLink: string;
  isActive: boolean;
};

const EMPTY: Draft = {
  eyebrow: '', title: '', subtitle: '', imageUrl: '', showOverlay: true,
  ctaLabel: '', ctaLink: '', secondaryCtaLabel: '', secondaryCtaLink: '', isActive: true,
};

function toDraft(b: BannerRow): Draft {
  return {
    id: b.id,
    eyebrow: b.eyebrow ?? '',
    title: b.title ?? '',
    subtitle: b.subtitle ?? '',
    imageUrl: b.imageUrl ?? '',
    showOverlay: b.showOverlay ?? true,
    ctaLabel: b.ctaLabel ?? '',
    ctaLink: b.ctaLink ?? '',
    secondaryCtaLabel: b.secondaryCtaLabel ?? '',
    secondaryCtaLink: b.secondaryCtaLink ?? '',
    isActive: b.isActive ?? true,
  };
}

function bodyOf(d: Draft) {
  return {
    eyebrow: d.eyebrow || undefined,
    title: d.title,
    subtitle: d.subtitle || undefined,
    imageUrl: d.imageUrl || undefined,
    showOverlay: d.showOverlay,
    ctaLabel: d.ctaLabel || undefined,
    ctaLink: d.ctaLink || undefined,
    secondaryCtaLabel: d.secondaryCtaLabel || undefined,
    secondaryCtaLink: d.secondaryCtaLink || undefined,
    isActive: d.isActive,
  };
}

async function uploadBannerBlob(blob: Blob): Promise<string> {
  const form = new FormData();
  form.append('file', new File([blob], `banner-${Date.now()}.jpg`, { type: 'image/jpeg' }));
  const r = await api.post('/uploads/banner', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  const data = r.data?.data ?? r.data;
  if (!data?.url) throw new Error('Server did not return a URL');
  return data.url as string;
}

export function BannerEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Edit mode reads the banner from the (cached) admin list — no dedicated GET:id needed.
  const { data: banners, isLoading } = useQuery({
    queryKey: ['admin-banners'],
    queryFn: () => request<BannerRow[]>({ url: '/banners' }),
    enabled: !isNew,
  });
  const existing = !isNew ? banners?.find((b) => String(b.id) === String(id)) : undefined;

  const [draft, setDraft] = useState<Draft | null>(isNew ? { ...EMPTY } : null);
  // Initialise the draft once the banner row arrives (edit mode).
  if (!isNew && draft === null && existing) setDraft(toDraft(existing));

  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const fileRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const saveMut = useMutation({
    mutationFn: (d: Draft) =>
      d.id
        ? request({ url: `/banners/${d.id}`, method: 'PUT', data: bodyOf(d) })
        : request({ url: '/banners', method: 'POST', data: bodyOf(d) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-banners'] });
      qc.invalidateQueries({ queryKey: ['public-banners'] });
      toast.success(isNew ? 'Banner created' : 'Banner updated');
      navigate('/admin/banners');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Could not save banner'),
  });

  const pickFile = (file?: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(String(reader.result));
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onCropped = async (blob: Blob) => {
    setCropSrc(null);
    setUploading(true);
    try {
      set({ imageUrl: await uploadBannerBlob(blob) });
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message ?? e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const previewBanner: Banner = useMemo(() => ({
    id: 'preview',
    eyebrow: draft?.eyebrow || null,
    title: draft?.title || 'Your headline here',
    subtitle: draft?.subtitle || null,
    imageUrl: draft?.imageUrl || null,
    showOverlay: draft?.showOverlay ?? true,
    ctaLabel: draft?.ctaLabel || null,
    ctaLink: draft?.ctaLink || null,
    secondaryCtaLabel: draft?.secondaryCtaLabel || null,
    secondaryCtaLink: draft?.secondaryCtaLink || null,
  }), [draft]);

  // Esc closes the full-screen preview.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  if (!isNew && isLoading && !draft) {
    return <div className="mx-auto max-w-6xl"><div className="h-64 animate-pulse rounded-card border border-line bg-surface" /></div>;
  }
  if (!isNew && !isLoading && !existing) {
    return (
      <div className="mx-auto max-w-6xl text-center">
        <p className="text-lg font-semibold text-content">Banner not found</p>
        <Button className="mt-4" variant="default" icon={<ArrowLeft />} onClick={() => navigate('/admin/banners')}>Back to banners</Button>
      </div>
    );
  }
  if (!draft) return null;

  const titleValid = draft.title.trim().length > 0;

  return (
    <div className="mx-auto max-w-7xl pb-10">
      {/* Sticky action header */}
      <div className="sticky top-16 z-30 -mx-4 mb-6 flex items-center justify-between gap-3 border-b border-line bg-canvas/85 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <button onClick={() => navigate('/admin/banners')} className="inline-flex items-center gap-2 text-sm font-semibold text-muted hover:text-brand-700">
          <ArrowLeft className="size-4" /> Banners
        </button>
        <div className="flex items-center gap-2">
          <Button variant="default" onClick={() => navigate('/admin/banners')}>Cancel</Button>
          <Button variant="primary" loading={saveMut.isPending} disabled={!titleValid} onClick={() => saveMut.mutate(draft)}>
            {isNew ? 'Create banner' : 'Save changes'}
          </Button>
        </div>
      </div>

      <h1 className="mb-1 text-2xl font-extrabold text-content">{isNew ? 'New banner' : 'Edit banner'}</h1>
      <p className="mb-6 text-sm text-muted">Design a banner, upload it, and it shows full-width in the home-page carousel.</p>

      <div className="grid gap-8 xl:grid-cols-[1fr_1.15fr]">
        {/* ───── Form ───── */}
        <div className="order-2 space-y-5 xl:order-1">
          {/* Image */}
          <Field label="Banner image" hint="Use a high-resolution 16:9 image — at least 1920×1080 (2560×1440 stays crisp on large/4K screens). You'll crop after selecting.">

            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])} />
            {draft.imageUrl ? (
              <div className="relative overflow-hidden rounded-card border border-line">
                <img src={draft.imageUrl} alt="banner" className="aspect-video w-full object-cover" />
                <div className="absolute right-2 top-2 flex gap-2">
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="rounded-control bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur hover:bg-black/70">Replace</button>
                  <button type="button" aria-label="Remove image" onClick={() => set({ imageUrl: '' })}
                    className="grid size-7 place-items-center rounded-control bg-black/55 text-white backdrop-blur hover:bg-black/70"><X className="size-4" /></button>
                </div>
              </div>
            ) : uploading ? (
              <div className="grid aspect-video place-items-center rounded-card border-2 border-dashed border-brand-300 bg-brand-50/50 text-sm text-muted dark:bg-brand-900/10">Uploading…</div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="grid aspect-video w-full place-items-center rounded-card border-2 border-dashed border-line bg-surface-2 text-muted transition-colors hover:border-brand-300 hover:text-brand-700">
                <div className="flex flex-col items-center gap-2">
                  <ImagePlus className="size-8" />
                  <span className="text-sm font-semibold">Upload banner image</span>
                  <span className="text-xs">PNG / JPG / WEBP · up to 8 MB</span>
                </div>
              </button>
            )}
          </Field>

          {/* Overlay toggle */}
          <label className="flex items-start gap-3 rounded-card border border-line bg-surface-2 p-3">
            <Switch checked={draft.showOverlay} onCheckedChange={(v) => set({ showOverlay: v })} />
            <span>
              <span className="block text-sm font-semibold text-content">Show text &amp; buttons over the image</span>
              <span className="block text-xs text-muted">Turn off if your image already includes the text.</span>
            </span>
          </label>

          {/* Text */}
          <div className={cn('space-y-4', draft.imageUrl && !draft.showOverlay && 'opacity-60')}>
            <Field label="Eyebrow" hint="Small label above the headline">
              <Input value={draft.eyebrow} onChange={(e) => set({ eyebrow: e.target.value })} placeholder="Sustainable Bio-Innovation for Indian Farmers" />
            </Field>
            <Field label="Headline" required hint="Also used as this banner's name. Use line breaks for stacked words.">
              <Textarea rows={3} value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder={'Nurture.\nGrow.\nProtect.'} />
            </Field>
            <Field label="Subtitle">
              <Textarea rows={3} value={draft.subtitle} onChange={(e) => set({ subtitle: e.target.value })} placeholder="A short supporting line under the headline." />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Primary button text"><Input value={draft.ctaLabel} onChange={(e) => set({ ctaLabel: e.target.value })} placeholder="Explore Products" /></Field>
              <Field label="Primary button link" hint="/catalog or https://…"><Input value={draft.ctaLink} onChange={(e) => set({ ctaLink: e.target.value })} placeholder="/catalog" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Secondary button text"><Input value={draft.secondaryCtaLabel} onChange={(e) => set({ secondaryCtaLabel: e.target.value })} placeholder="Our Story" /></Field>
              <Field label="Secondary button link"><Input value={draft.secondaryCtaLink} onChange={(e) => set({ secondaryCtaLink: e.target.value })} placeholder="/about" /></Field>
            </div>
          </div>

          <label className="flex items-center gap-3">
            <Switch checked={draft.isActive} onCheckedChange={(v) => set({ isActive: v })} />
            <span className="text-sm font-medium text-content">Active (show on the site)</span>
          </label>
        </div>

        {/* ───── Large live preview ───── */}
        <div className="order-1 xl:order-2">
          <div className="xl:sticky xl:top-32">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-content">
                <Eye className="size-4 text-brand-600" /> Live preview
              </span>
              <button type="button" onClick={() => setFullscreen(true)}
                className="inline-flex items-center gap-1.5 rounded-control border border-line bg-surface px-2.5 py-1 text-xs font-semibold text-content transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20">
                <Maximize2 className="size-3.5" /> Full screen
              </button>
            </div>
            {/* Non-interactive: real slide markup, exactly as it renders on the home page. */}
            <button type="button" onClick={() => setFullscreen(true)} title="Open full-screen preview"
              className="group relative block aspect-[16/9] w-full overflow-hidden rounded-card border border-line bg-brand-900 shadow-card">
              <div className="pointer-events-none size-full">
                <BannerSlideView b={previewBanner} />
              </div>
              {!draft.isActive && (
                <span className="absolute left-3 top-3 rounded-pill bg-black/55 px-2.5 py-1 text-[11px] font-bold text-white">Hidden</span>
              )}
              <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-pill bg-black/45 px-2.5 py-1 text-[11px] font-bold text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                <Maximize2 className="size-3.5" /> Full screen
              </span>
            </button>
            <p className="mt-2 text-xs text-muted">
              The actual home-page hero markup at panel size. Click <strong>Full screen</strong> to see it edge-to-edge at real size.
            </p>
          </div>
        </div>
      </div>

      {cropSrc && (
        <ImageCropper imageSrc={cropSrc} aspectRatio={16 / 9} onCropComplete={onCropped} onCancel={() => setCropSrc(null)} />
      )}

      {/* Full-screen preview — the banner exactly as it appears on the home page, at real width. */}
      {fullscreen && (
        <div className="fixed inset-0 z-[300] flex flex-col bg-canvas">
          <div className="flex items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-content">
              <Eye className="size-4 text-brand-600" /> Full-screen preview
              <span className="hidden font-normal text-muted sm:inline">— exactly as it appears on the home-page hero</span>
            </span>
            <button type="button" onClick={() => setFullscreen(false)}
              className="inline-flex items-center gap-2 rounded-control border border-line bg-surface px-3 py-1.5 text-sm font-semibold text-content transition-colors hover:bg-surface-2">
              <X className="size-4" /> Close <span className="hidden text-muted sm:inline">(Esc)</span>
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {/* Same sizing as the live carousel section → 1:1 with the real site at this viewport width. */}
            <div className="pointer-events-none relative aspect-[16/9] max-h-[82vh] min-h-[440px] w-full overflow-hidden bg-brand-900">
              <BannerSlideView b={previewBanner} />
              {!draft.isActive && (
                <span className="absolute right-4 top-4 rounded-pill bg-black/55 px-3 py-1 text-xs font-bold text-white">Hidden — won't show on the site</span>
              )}
            </div>
            <div className="border-t border-line bg-canvas px-6 py-8 text-center text-sm text-muted">
              This is the hero at full width. Your other landing sections continue below it on the live page.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
